/* ==========================================================================
   Undefeated — accessibility remediation, ticket P2-6 (+ N-13)
   Sunrise Integration · WCAG 2.2 AA · 2026-09-01

   Mobile navigation: keyboard operability and focus management.

   Deploy as assets/undtd-a11y.js. Placement does not matter — head or
   body, with or without defer — because initialisation waits for the DOM
   (see the readyState guard at the bottom). theme_undtd.js sits in the
   <head> carrying `defer`, so it has already executed by DOMContentLoaded
   and this script always runs after it.

   It does not modify theme_undtd.js. It watches #mobile-dropdown's class
   attribute, so it reacts to the theme's own open/close regardless of how
   that is triggered, and closes the panel by clicking the theme's own
   button rather than duplicating the theme's class toggling.

   window.__undtdA11yNav reports what happened, for verification:
     'active'   — observer attached, panel found
     'no-panel' — #mobile-dropdown or #NavDropdown absent on this template

   ---------------------------------------------------------------------------
   WHAT WAS MEASURED ON THE LIVE SITE (2026-09-01)

   #mobile-dropdown computes to height:0; overflow:hidden — but
   display:block and visibility:visible. Its descendants therefore keep
   visibility:visible, non-zero rects and an offsetParent, which means all
   217 of them stay in the tab order WHILE THE MENU IS CLOSED.

   Verified with real key presses at 402px wide: one Tab past the "Site
   navigation" button lands on "Shop" inside the closed panel, and the next
   four Tabs walk to "Contact" — index 4 of 217 — every one of them clipped
   out of view, so no focus indicator is visible anywhere on screen.

   The same is true at 1050px, where #NavDropdown is display:none. Desktop
   keyboard users tab through 217 invisible stops they cannot even open.

   This is broader than the audit's framing of P2-6 (which described the
   OPEN state). Logged as N-13 and the reason this ships ahead of the rest.

   Open state, also measured: panel animates to 688px of a 749px viewport,
   so it obscures the page; aria-expanded flips correctly to "true";
   Escape does nothing; focus never enters the panel and is never restored.
   ========================================================================== */

(function () {
  'use strict';

  function init() {

  var PANEL_ID = 'mobile-dropdown';
  var BTN_ID = 'NavDropdown';
  var OPEN_CLASS = 'open';
  /* Everything painted above the drawer, so it stays reachable while open. */
  var VISIBLE_CHROME = '.header_content_container';

  var panel = document.getElementById(PANEL_ID);
  var btn = document.getElementById(BTN_ID);
  if (!panel || !btn) { window.__undtdA11yNav = 'no-panel'; return; }

  var FOCUSABLE = [
    'a[href]', 'button:not([disabled])', 'input:not([disabled])',
    'select:not([disabled])', 'textarea:not([disabled])', 'summary',
    '[tabindex]:not([tabindex^="-"])', '[contenteditable="true"]'
  ].join(',');

  var supportsInert = 'inert' in HTMLElement.prototype;
  var wasOpen = null;   /* null = first pass, so we do not steal focus on load */

  function isOpen() {
    return panel.classList.contains(OPEN_CLASS);
  }

  function focusablesIn(root) {
    return Array.prototype.filter.call(
      root.querySelectorAll(FOCUSABLE),
      function (el) {
        if (el.closest('[inert]')) return false;
        var cs = window.getComputedStyle(el);
        if (cs.visibility === 'hidden' || cs.display === 'none') return false;
        return !!el.offsetParent || cs.position === 'fixed';
      }
    );
  }

  /* ---- closed: out of the tab order ------------------------------------ */

  function deactivate() {
    if (supportsInert) {
      panel.inert = true;
    } else {
      /* CSS fallback lives in undtd-a11y.css:
         #mobile-dropdown.a11y-nav-closed{visibility:hidden}
         visibility:hidden is inherited, so descendants leave the tab order
         in every engine. inert has no visual effect, so the two paths look
         identical; only the fallback needs the stylesheet. */
      panel.classList.add('a11y-nav-closed');
    }
    panel.setAttribute('aria-hidden', 'true');
  }

  function activate() {
    if (supportsInert) {
      panel.inert = false;
    } else {
      panel.classList.remove('a11y-nav-closed');
    }
    panel.removeAttribute('aria-hidden');
  }

  /* ---- open: focus in, Escape out, obscured page unreachable ----------- */

  function onKeydown(e) {
    if (!isOpen()) return;

    if (e.key === 'Escape' || e.key === 'Esc') {
      e.preventDefault();
      /* Close through the theme's own handler so the animation, the
         aria-expanded flip and body.js-drawer-open-down all stay in sync. */
      btn.click();
      /* The observer restores focus to btn when the class drops. */
      return;
    }

    if (e.key !== 'Tab') return;

    /* The drawer covers 688px of a 749px viewport. Keeping focus inside the
       header chrome plus the panel means focus never lands on something the
       drawer is painted over — 2.4.11 — while everything still visible on
       screen stays reachable. */
    var chrome = document.querySelector(VISIBLE_CHROME);
    var loop = (chrome ? focusablesIn(chrome) : [btn]).concat(focusablesIn(panel));
    if (loop.indexOf(btn) === -1) loop.unshift(btn);
    if (!loop.length) return;

    var first = loop[0];
    var last = loop[loop.length - 1];
    var i = loop.indexOf(document.activeElement);

    if (i === -1) {
      /* Focus is outside the loop — an injected node, or the page behind the
         drawer. Pull it back to whichever end the user was heading for. */
      e.preventDefault();
      (e.shiftKey ? last : first).focus();
    } else if (e.shiftKey && i === 0) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && i === loop.length - 1) {
      e.preventDefault();
      first.focus();
    }
    /* Anywhere in the middle: let the browser move focus itself. */
  }

  function opened() {
    activate();
    var targets = focusablesIn(panel);
    if (targets.length) targets[0].focus();
  }

  function closed() {
    /* Restore focus BEFORE making the panel inert, or the browser drops
       focus to <body> and the user loses their place entirely. */
    if (panel.contains(document.activeElement)) btn.focus();
    deactivate();
  }

  function sync() {
    var open = isOpen();
    if (open === wasOpen) return;
    if (wasOpen === null) {
      /* First pass on load: set the closed state without touching focus. */
      if (open) { activate(); } else { deactivate(); }
    } else if (open) {
      opened();
    } else {
      closed();
    }
    wasOpen = open;
  }

  new MutationObserver(sync).observe(panel, {
    attributes: true,
    attributeFilter: ['class']
  });

  document.addEventListener('keydown', onKeydown, true);

  sync();
    window.__undtdA11yNav = 'active';
  }

  /* The panel lives in the <body>, so initialising must not race the parser.
     Shopify's script_tag filter emits a plain <script src> with no defer, and
     theme.liquid loads it from the <head> — so on a first pass the panel does
     not exist yet and every getElementById returns null. Waiting for
     DOMContentLoaded makes the script correct wherever the tag is placed. */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();

/* ============================================================================
   P1-2 — autoplaying looping videos (WCAG 2.2.2 Pause, Stop, Hide)

   The videos autoplay. Every one gets a visible, keyboard-operable control that
   stops it and resets it to its first frame, and prefers-reduced-motion is
   honoured — which CSS cannot do, because CSS cannot stop an autoplay attribute.

   REPLACES the previous P1-2 block in assets/undtd-a11y.js. Delete that block
   before pasting this one — do not append both. Pairs with the P1-2 block in
   assets/undtd-a11y.css, also replaced.

   Requires autoplay to be present in snippets/graphic-cover.liquid.

   Per the P2-6 deploy note, script_tag emits no defer and theme.liquid loads
   this from the head, so it waits for DOMContentLoaded itself.
   window.__undtdA11yMotion reports 'active' once attached.
   ============================================================================ */
(function () {
  'use strict';

  var HOST_SEL    = '.content-card';
  var VIDEO_SEL   = '.content-card video';
  var DONE_ATTR   = 'data-undtd-motion';
  var INTENT_ATTR = 'data-undtd-intent';   /* 'play' or 'stop', set by a person */

  var ICON_STOP = '<svg class="undtd-motion-ico-stop" viewBox="0 0 12 12" aria-hidden="true" focusable="false">' +
                  '<rect x="1.75" y="1.75" width="8.5" height="8.5" rx="1"/></svg>';
  var ICON_PLAY = '<svg class="undtd-motion-ico-play" viewBox="0 0 12 12" aria-hidden="true" focusable="false">' +
                  '<path d="M2.5 1.2 10.5 6l-8 4.8z"/></svg>';

  var mq = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;
  function reduced() { return !!(mq && mq.matches); }

  /* The card title, not the video's own aria-label: the aria-label is long prose
     and on card 2 still carries the "Undeafeated" typo (N-2). Two videos sit in
     one grid, so an unqualified "Stop video" would name both controls
     identically. */
  function nameFor(host) {
    var t = host.querySelector('.content-card__title');
    var label = t && t.textContent;
    if (!label) {
      var a = host.querySelector('a[aria-label]');
      label = a && a.getAttribute('aria-label');
    }
    return label ? label.replace(/\s+/g, ' ').trim() : '';
  }

  /* The name says what activating the control will DO, and it says "Stop"
     rather than "Pause" because the video is reset rather than held mid-frame.
     Toggling by name is the clearer convention for media controls; mixing that
     with aria-pressed is an anti-pattern. */
  function setState(btn, stopped) {
    if (!btn) return;
    var verb = stopped ? 'Play' : 'Stop';
    var name = btn.getAttribute('data-undtd-name');
    btn.setAttribute('data-state', stopped ? 'stopped' : 'playing');
    btn.setAttribute('aria-label', name ? verb + ' video: ' + name : verb + ' background video');
  }

  function enhance(video) {
    if (video.getAttribute(DONE_ATTR)) return;

    var host = video.closest(HOST_SEL);
    if (!host) return;

    video.setAttribute(DONE_ATTR, 'done');
    host.classList.add('undtd-has-motion');

    var btn = document.createElement('button');
    btn.setAttribute('type', 'button');
    btn.className = 'undtd-motion-btn';
    btn.setAttribute('data-undtd-name', nameFor(host));
    btn.innerHTML = ICON_STOP + ICON_PLAY;
    /* An autoplaying video may not have started at this exact instant, but it is
       about to. Labelling it "Stop" now avoids a flash of the wrong control, and
       the media listeners below correct it if playback never happens. */
    setState(btn, video.hasAttribute('autoplay') ? false : video.paused);

    /* Sibling of the graphic link, never a descendant: a button inside an <a> is
       nested interactive content and gives an unpredictable a11y tree. Placed
       straight after that link so it lands next to the thing it controls in the
       tab order — confirmed with real key presses on the live site.

       Injected rather than rendered in Liquid: a server-rendered button would be
       a visible control that does nothing if the script failed. NOTE the trade
       this version accepts, which the play-on-press version did not: if this
       script fails to load, the videos autoplay with no way to stop them and
       2.2.2 fails outright. That is an argument for keeping this file small,
       dependency-free, and early. */
    var graphicLink = null;
    for (var i = 0; i < host.children.length; i++) {
      if (host.children[i].tagName === 'A') { graphicLink = host.children[i]; break; }
    }
    if (graphicLink && graphicLink.insertAdjacentElement) {
      graphicLink.insertAdjacentElement('afterend', btn);
    } else {
      host.appendChild(btn);
    }

    /* play() is not a promise that it played. It can be refused outright, and it
       can also resolve while the element stays paused. Either way the label must
       not go on claiming the video is running. */
    function start() {
      video.setAttribute(INTENT_ATTR, 'play');
      setState(btn, false);
      var p;
      try { p = video.play(); } catch (e) {}
      if (p && p.then) {
        p.then(
          function () { if (video.paused) setState(btn, true); },
          function () { setState(btn, true); }
        );
      }
    }

    /* Stop, not pause: the video is reset to its first frame rather than left
       holding whatever frame it happened to reach, which reads as a stopped
       video instead of a stuck one.

       Deliberately NOT restoring the poster attribute's image. That looks
       possible — swap in a fresh element and it renders the poster — but it only
       works on a cold cache. Once the clip is buffered a fresh element reports
       readyState 4 immediately and paints frame 0 anyway, so the poster restore
       silently stops happening for exactly the visitors who have seen the video,
       which with autoplay is everyone. Measured on the live site. Resetting to
       frame 0 does the same job deterministically. */
    function stop() {
      video.setAttribute(INTENT_ATTR, 'stop');
      try {
        video.pause();
        video.currentTime = 0;
      } catch (e) {}
      setState(btn, true);
    }

    video.addEventListener('pause', function () { setState(btn, true); });

    video.addEventListener('play', function () {
      var intent = video.getAttribute(INTENT_ATTR);
      /* An explicit stop outranks anything that tries to restart it. */
      if (intent === 'stop') {
        try { video.pause(); } catch (e) {}
        setState(btn, true);
        return;
      }
      /* Reduced motion suppresses playback nobody asked for. It does not
         override an explicit press: the setting is there to stop unrequested
         motion, not to take the control away from the person. */
      if (reduced() && intent !== 'play') {
        try { video.pause(); video.currentTime = 0; } catch (e) {}
        setState(btn, true);
        return;
      }
      setState(btn, false);
    });

    btn.addEventListener('click', function (e) {
      e.preventDefault();
      e.stopPropagation();
      /* Branch on what the control is telling the person, not on the element's
         paused flag: the two can disagree when playback was refused, and the
         button must do what its own label promises. */
      if (btn.getAttribute('data-state') === 'stopped') { start(); } else { stop(); }
    });

    /* Someone who has asked their OS for reduced motion should never have to go
       find the control. */
    if (reduced()) stop();

    host.__undtdStop = stop;
  }

  function scan() {
    var vids = document.querySelectorAll(VIDEO_SEL);
    for (var i = 0; i < vids.length; i++) enhance(vids[i]);
  }

  function init() {
    scan();

    if (mq) {
      var onChange = function () {
        /* Turning the setting ON stops everything. Turning it OFF does not start
           anything: motion resumes only on a press. */
        if (!reduced()) return;
        var hosts = document.querySelectorAll(HOST_SEL);
        for (var i = 0; i < hosts.length; i++) {
          if (hosts[i].__undtdStop) hosts[i].__undtdStop();
        }
      };
      if (mq.addEventListener) mq.addEventListener('change', onChange);
      else if (mq.addListener) mq.addListener(onChange);
    }

    /* Sections re-render on theme-editor edits and on some AJAX paths, which
       drops the control. Same MutationObserver approach as P2-6. */
    if (window.MutationObserver) {
      var pending = null;
      new MutationObserver(function () {
        if (pending) return;
        pending = setTimeout(function () { pending = null; scan(); }, 120);
      }).observe(document.body, { childList: true, subtree: true });
    }

    window.__undtdA11yMotion = 'active';
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
}());